import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Plus, Video, Trash2 } from "lucide-react";
import api from "../lib/api";
import { cadenceLabel } from "../lib/cadence";
import { Button } from "../components/ui/button";
import StartSessionPrompt from "../components/StartSessionPrompt";
import AddToCalendar from "../components/AddToCalendar";
import RenameMeeting from "../components/RenameMeeting";
import CreateSeriesDialog from "../components/CreateSeriesDialog";
import { sessionMoment, fmtDateTime, sessionSortTime, sessionLateness } from "../lib/dates";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const FILTER_KEY = "boardroom_sessions_filter";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Anything not yet held is "upcoming" — scheduled, drafting or live right now. */
const isPast = (m) => m.status === "completed";

export default function MeetingHistory() {  const [meetings, setMeetings] = useState([]);
  const [trashCount, setTrashCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState(() => {
    try {
      const v = localStorage.getItem(FILTER_KEY);
      return v === "upcoming" || v === "past" ? v : "all";
    } catch {
      return "all";
    }
  });

  const pickFilter = (v) => {
    setFilter(v);
    try {
      localStorage.setItem(FILTER_KEY, v);
    } catch { /* private mode — the choice just doesn't persist */ }
  };

  useEffect(() => {
    Promise.all([
      api.get("/meetings"),
      api.get("/meetings/trash").catch(() => ({ data: [] })),
    ]).then(([m, t]) => {
      setMeetings(m.data);
      setTrashCount((t.data || []).length);
    }).finally(() => setLoading(false));
  }, []);

  // Poll for minutes-ready state while any completed session is still drafting.
  // Fires every 5s until every completed session has minutes, then stops. No
  // polling load if the archive is idle.
  useEffect(() => {
    const drafting = meetings.some((m) => m.status === "completed" && !m.minutes);
    if (!drafting) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get("/meetings");
        setMeetings(r.data);
      } catch (e) {
        // silent — polling will retry
      }
    }, 5000);
    return () => clearInterval(t);
  }, [meetings]);

  const recordingCount = meetings.filter((m) => m.recording_path).length;

  // Upcoming scheduled sessions first (soonest at the top), then everything
  // else newest-first. Keeps a booked quarter readable.
  const ordered = [...meetings].sort((a, b) => {
    const aSched = a.status === "scheduled" && a.scheduled_for;
    const bSched = b.status === "scheduled" && b.scheduled_for;
    if (aSched && bSched) return new Date(a.scheduled_for) - new Date(b.scheduled_for);
    if (aSched) return -1;
    if (bSched) return 1;
    return sessionSortTime(b) - sessionSortTime(a);
  });

  const pastCount = meetings.filter(isPast).length;
  const upcomingCount = meetings.length - pastCount;
  const visible = ordered.filter((m) => (
    filter === "past" ? isPast(m) : filter === "upcoming" ? !isPast(m) : true
  ));
  const FILTERS = [
    { key: "all", label: "All", count: meetings.length },
    { key: "upcoming", label: "Upcoming", count: upcomingCount },
    { key: "past", label: "Past", count: pastCount },
  ];

  const downloadCancelIcs = async (meeting) => {
    try {
      const userId = localStorage.getItem("boardroom_user_id");
      const r = await fetch(`${BACKEND}/api/meetings/${meeting.id}/calendar.ics?cancel=true`, {
        credentials: "include",
        headers: userId ? { "X-User-Id": userId } : {},
      });
      if (!r.ok) throw new Error("cancel ics failed");
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `boardroom-cancel-${meeting.id.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Cancellation downloaded — open it to clear the event from your calendar.");
    } catch (e) {
      toast.error("Could not build the cancellation file");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const deleted = pendingDelete;
    setDeleting(true);
    try {
      await api.delete(`/meetings/${deleted.id}`);
      setMeetings((prev) => prev.filter((x) => x.id !== deleted.id));
      setTrashCount((c) => c + 1);
      // A session that was booked may also be sitting in the user's own
      // calendar app; offer the matching cancellation so it doesn't linger.
      if (deleted.scheduled_for) {
        toast.success("Moved to trash · removed from your Boardroom calendar.", {
          duration: 9000,
          action: {
            label: "Remove from my calendar app",
            onClick: () => downloadCancelIcs(deleted),
          },
        });
      } else {
        toast.success("Moved to trash · restorable for 30 days.", {
          action: {
            label: "View trash",
            onClick: () => (window.location.href = "/meetings/trash"),
          },
        });
      }
      setPendingDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="meetings-history-root">
      <header className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-eyebrow">Archive</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Session minutes</h1>
          <p className="text-[#888] mt-4 max-w-xl">
            Every session is on the record. The minutes never lie.
            {recordingCount > 0 && (
              <span className="block text-xs font-mono text-[#c89f45] mt-2" data-testid="archive-recording-count">
                {recordingCount} SESSION{recordingCount === 1 ? "" : "S"} WITH RECORDING · CLICK ANY ROW TO REPLAY
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <CreateSeriesDialog onCreated={() => api.get("/meetings").then((r) => setMeetings(r.data))} />
          <StartSessionPrompt
            testId="archive-new-session"
            className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-10 px-4"
          >
            <Plus size={14} className="mr-1.5" /> New session
          </StartSessionPrompt>
        </div>
      </header>

      {trashCount > 0 && (
        <div className="flex justify-end -mt-6">
          <Link
            to="/meetings/trash"
            className="text-[10px] font-mono uppercase tracking-widest text-[#888] hover:text-[#e07b6a] transition-colors inline-flex items-center gap-1"
            data-testid="archive-trash-link"
          >
            <Trash2 size={11} />
            TRASH · {trashCount}
          </Link>
        </div>
      )}

      {loading ? (
        <div className="surface rounded-md p-8 text-sm text-[#666]">Loading…</div>
      ) : meetings.length === 0 ? (
        <div className="surface rounded-md p-16 text-center" data-testid="history-empty">
          <p className="font-serif text-2xl">The minutes are blank.</p>
          <p className="text-sm text-[#888] mt-2">Hold your first session to start the archive.</p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter sessions" data-testid="history-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => pickFilter(f.key)}
                aria-pressed={filter === f.key}
                className={`min-h-[40px] px-4 rounded-full border text-sm transition-colors flex items-center gap-2 ${
                  filter === f.key
                    ? "border-[#c89f45] bg-[#c89f45]/10 text-[#c89f45]"
                    : "border-[#2a2a2a] text-[#aaa] hover:border-[#3a3a3a] hover:text-[#eaeaea]"
                }`}
                data-testid={`history-filter-${f.key}`}
              >
                {f.label}
                <span className="text-[10px] font-mono tracking-widest text-[#8a8a8a]">{f.count}</span>
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <div className="surface rounded-md p-12 text-center" data-testid="history-filter-empty">
              <p className="font-serif text-xl">
                {filter === "upcoming" ? "Nothing on the calendar yet." : "No sessions held yet."}
              </p>
              <p className="text-sm text-[#888] mt-2">
                {filter === "upcoming"
                  ? "Book your next session and it'll show up here."
                  : "Held sessions and their minutes land here once you adjourn."}
              </p>
            </div>
          ) : (
          <div className="space-y-3">
          {visible.map((m) => (
            <div
              key={m.id}
              className="surface surface-hover rounded-md p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between transition-colors"
              data-testid={`history-row-${m.id}`}
            >
              <Link
                to={m.status === "completed" ? `/meetings/${m.id}/minutes` : `/meetings/${m.id}`}
                className="flex-1 min-w-0 block"
                data-testid={`history-row-link-${m.id}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="label-eyebrow">{cadenceLabel(m.cadence)} · {m.meeting_type}</span>
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${
                    m.status === "completed" ? "bg-[#1B3B36] text-[#9bd3c4]" :
                    m.status === "active" ? "bg-[#c89f45]/20 text-[#c89f45]" :
                    "bg-[#1a1a1a] text-[#888]"
                  }`}>{m.status}</span>
                  {m.recording_path && (
                    <span
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#2a1a1a] text-[#e07b6a] flex items-center gap-1"
                      title="This session has a recording you can replay"
                      data-testid={`history-rec-badge-${m.id}`}
                    >
                      <Video size={9} />
                      REC
                    </span>
                  )}
                  {/* Minutes-status pill — makes it obvious when a session
                      that was just adjourned is still drafting vs ready. */}
                  {m.status === "completed" && (
                    m.minutes ? (
                      <span
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#c89f45]/15 text-[#c89f45]"
                        title="AI minutes are ready to review"
                        data-testid={`history-minutes-ready-${m.id}`}
                      >
                        MINUTES READY
                      </span>
                    ) : (
                      <span
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#1a1a1a] text-[#888] animate-pulse"
                        title="Minutes are still being drafted"
                        data-testid={`history-minutes-drafting-${m.id}`}
                      >
                        DRAFTING…
                      </span>
                    )
                  )}
                  {sessionLateness(m) && (
                    <span
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border border-[#3a3320] text-[#8f8266]"
                      title={`Held ${sessionLateness(m).days} day${sessionLateness(m).days === 1 ? "" : "s"} after the booked slot`}
                      data-testid={`history-late-${m.id}`}
                    >
                      {sessionLateness(m).label}
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-xl sm:text-2xl mt-2 line-clamp-2 sm:truncate">{m.title}</h3>
                <p className="text-xs text-[#8a8a8a] mt-2 font-mono" data-testid={`history-row-when-${m.id}`}>
                  {(() => { const w = sessionMoment(m); return `${w.label.toUpperCase()} · ${fmtDateTime(w.iso)}`; })()}
                  {m.duration_seconds ? ` · ${Math.round(m.duration_seconds / 60)} min` : ""}
                </p>
              </Link>
              <div className="flex items-center gap-1 mt-4 sm:mt-0 sm:ml-4 shrink-0 w-full sm:w-auto justify-end">
                {/* While renaming on a phone the editor takes the whole row —
                    the other controls would push it off the left edge. */}
                <RenameMeeting
                  meeting={m}
                  compact
                  onEditingChange={(v) => setRenamingId(v ? m.id : null)}
                  onUpdated={(next, opts) => {
                    if (opts?.refetch) { api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => {}); return; }
                    setMeetings((all) => all.map((x) => x.id === next.id ? next : x));
                  }}
                />
                {m.status === "scheduled" && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className={renamingId === m.id ? "hidden sm:block" : undefined}
                  >
                    <AddToCalendar
                      meeting={m}
                      compact
                      onUpdated={(next, opts) => {
                    if (opts?.refetch) { api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => {}); return; }
                    setMeetings((all) => all.map((x) => x.id === next.id ? next : x));
                  }}
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPendingDelete(m); }}
                  disabled={deleting && pendingDelete?.id === m.id}
                  className={`${renamingId === m.id ? "hidden sm:flex" : "flex"} h-10 w-10 sm:h-9 sm:w-9 shrink-0 rounded-md border border-transparent text-[#666] hover:text-[#e07b6a] hover:border-[#e07b6a]/40 hover:bg-[#2a1a1a]/40 disabled:opacity-40 disabled:cursor-not-allowed items-center justify-center transition-colors`}
                  aria-label="Delete session"
                  title="Delete session"
                  data-testid={`history-delete-${m.id}`}
                >
                  <Trash2 size={16} />
                </button>
                <ArrowUpRight size={18} className="text-[#666] hidden sm:block" />
              </div>
            </div>
          ))}
          </div>
          )}
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#fafafa]" data-testid="history-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Move this session to trash?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#888] pt-2">
              {pendingDelete?.title ? `"${pendingDelete.title}" — ` : ""}
              It'll live in the trash for 30 days. You can restore it anytime before then;
              after that, the recording, transcript, minutes, and action items are purged permanently.
              {pendingDelete?.scheduled_for && (
                <span className="block mt-2 text-[#bbb]">
                  It comes off your Boardroom calendar and reminders straight away. If you also added it to
                  Apple/Google Calendar, we'll offer a cancellation file to clear it there.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 [&>button]:min-h-[44px] sm:[&>button]:min-h-0">
            <AlertDialogCancel
              className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white"
              data-testid="history-delete-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium"
              data-testid="history-delete-confirm"
            >
              {deleting ? "Moving…" : "Move to trash"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
